import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CTASection = ({ onJoinVendor, onBrowseVendors, onPlanWithAI }) => {
  const ctaBlocks = [
    {
      id: 'browse',
      title: 'Find Your Perfect Vendors',
      subtitle: 'Discover & Connect',
      description: 'Browse thousands of verified South Asian wedding vendors across the U.S. Filter by culture, location, budget, and more.',
      icon: 'Search',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      buttonText: 'Browse All Vendors',
      buttonVariant: 'primary',
      buttonIcon: 'ArrowRight',
      action: onBrowseVendors,
      gradient: 'from-primary-500 to-primary-600',
      features: ['3,000+ Verified Vendors', 'Cultural Specialization', 'Instant Messaging', 'Real Reviews']
    },
    {
      id: 'vendor',
      title: 'Grow Your Wedding Business',
      subtitle: 'Join Our Marketplace',
      description: 'Connect with engaged couples actively planning their South Asian weddings. Showcase your services and grow your business.',
      icon: 'UserPlus',
      image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800&q=80',
      buttonText: 'Join as Vendor',
      buttonVariant: 'secondary',
      buttonIcon: 'Plus',
      action: onJoinVendor,
      gradient: 'from-secondary-500 to-secondary-600',
      features: ['Free Profile Setup', 'Lead Generation', 'Booking Management', 'Marketing Tools']
    },
    {
      id: 'ai',
      title: 'Plan with AI Assistance',
      subtitle: 'Smart Wedding Planning',
      description: 'Get personalized vendor recommendations and planning assistance powered by AI that understands South Asian wedding traditions.',
      icon: 'Bot',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      buttonText: 'Try AI Planner',
      buttonVariant: 'outline',
      buttonIcon: 'Sparkles',
      action: onPlanWithAI,
      gradient: 'from-accent-500 to-accent-600',
      features: ['Cultural Expertise', 'Budget Optimization', 'Timeline Planning', 'Vendor Matching'],
      comingSoon: true
    }
  ];

  const handleCTAClick = (cta) => {
    cta.action?.();
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
            Everything You Need for Your Perfect Wedding
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-body">
            Whether you're planning your dream wedding or growing your vendor business, 
            ShaadiNest provides the tools and connections you need to succeed.
          </p>
        </div>

        {/* CTA Blocks Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {ctaBlocks.map((cta, index) => (
            <div
              key={cta.id}
              className={`relative bg-white rounded-2xl shadow-medium border border-border overflow-hidden group hover:shadow-strong transition-all duration-300 ${
                index === 0 ? 'lg:col-span-1' : ''
              }`}
            >
              {/* Background Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={cta.image}
                  alt={cta.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${cta.gradient} opacity-70`} />
                
                {/* Icon */}
                <div className="absolute top-6 left-6 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Icon name={cta.icon} size={28} color="#FFFFFF" />
                </div>

                {/* Coming Soon Badge */}
                {cta.comingSoon && (
                  <div className="absolute top-6 right-6 bg-warning text-warning-foreground px-3 py-1 rounded-full text-sm font-medium">
                    Coming Soon
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm font-medium text-primary mb-1">
                    {cta.subtitle}
                  </p>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-3">
                    {cta.title}
                  </h3>
                  <p className="text-text-secondary font-caption mb-4">
                    {cta.description}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <ul className="space-y-2">
                    {cta.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <Icon name="Check" size={14} color="#228B22" />
                        <span className="text-sm text-text-secondary font-caption">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <Button
                  variant={cta.buttonVariant}
                  size="lg"
                  iconName={cta.buttonIcon}
                  iconPosition="right"
                  iconSize={18}
                  fullWidth
                  onClick={() => handleCTAClick(cta)}
                  disabled={cta.comingSoon}
                >
                  {cta.buttonText}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 lg:p-12 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-6">
              <Icon name="Heart" size={40} color="#FFFFFF" />
            </div>
            
            <h3 className="font-heading font-bold text-3xl lg:text-4xl mb-4">
              Ready to Plan Your Dream Wedding?
            </h3>
            <p className="text-lg opacity-90 mb-8 font-body">
              Join thousands of couples who have found their perfect vendors through ShaadiNest. 
              Start your journey today and create memories that will last a lifetime.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="outline"
                size="xl"
                iconName="Search"
                iconSize={20}
                onClick={onBrowseVendors}
                className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20"
              >
                Start Planning Now
              </Button>
              <Button
                variant="ghost"
                size="xl"
                iconName="Play"
                iconSize={20}
                className="text-white hover:bg-white/10"
              >
                Watch Success Stories
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="text-2xl font-bold font-heading">50,000+</div>
                <div className="text-sm opacity-80 font-caption">Happy Couples</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold font-heading">3,000+</div>
                <div className="text-sm opacity-80 font-caption">Verified Vendors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold font-heading">25+</div>
                <div className="text-sm opacity-80 font-caption">Major Cities</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold font-heading">4.9★</div>
                <div className="text-sm opacity-80 font-caption">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;